// Tested by use-enforced-theme-state
import useAppConfig from '@rhg/hooks/use-app-config';
import ThemeProvider from '@rhg/libs/material/theme-provider';
import { useRouter } from 'next/router';
import '@rhg/libs/datadog';
import { FullWidthLinearProgress } from '@rhg/components/ui/progress';
import { useProvidedTheme } from '@rhg/hooks/use-provided-theme';
import useAuth from '@rhg/libs/auth0/use-auth';
import { Box } from '@rhg/libs/material';
import { ColorMode } from '@rhg/libs/material/theme';
import { routes } from '@rhg/utils/routes';
import { ReactNode, createContext } from 'react';

type PageThemeEnforcement = Record<
    string,
    { unauthenticated?: ColorMode; authenticated?: ColorMode }
>;

const enforcedPageThemes: PageThemeEnforcement = {
    [routes.registration]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.sso]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.ssoInfo]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.saml]: { unauthenticated: 'light', authenticated: 'light' },
    [routes.dashboard]: { unauthenticated: 'light', authenticated: undefined },
    [routes.standaloneScreening]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.standaloneResults]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.nonUsResident]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.login]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
    [routes.signup]: {
        unauthenticated: 'light',
        authenticated: 'light',
    },
};

function pageThemeMode(
    pathname: string,
    isAuthenticated: boolean,
    enforcedPageThemes: PageThemeEnforcement
) {
    const pageEnforcement = enforcedPageThemes[pathname];
    if (!pageEnforcement) {
        return undefined;
    }
    return isAuthenticated
        ? pageEnforcement.authenticated
        : pageEnforcement.unauthenticated;
}

export interface EnforcedThemeState {
    enforcedThemeMode?: ColorMode;
}
export const EnforcedThemeStateContext = createContext<EnforcedThemeState>(
    null!
);

export const EnforcedThemeProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { NEXT_PUBLIC_THEME_SELECTOR_ENABLED: isThemeSelectorEnabled } =
        useAppConfig();
    const { isAuthenticated, isLoading } = useAuth();
    const { pathname, query } = useRouter();

    const enforcedThemeMode = pageThemeMode(
        pathname,
        isAuthenticated,
        enforcedPageThemes
    );

    const theme = useProvidedTheme(
        enforcedThemeMode,
        isThemeSelectorEnabled,
        query.theme as string | undefined
    );

    return (
        <EnforcedThemeStateContext.Provider value={{ enforcedThemeMode }}>
            <ThemeProvider theme={theme}>
                {isLoading ? (
                    <Box sx={{ position: 'fixed', left: 0, top: 0 }}>
                        <FullWidthLinearProgress />
                    </Box>
                ) : (
                    children
                )}
            </ThemeProvider>
        </EnforcedThemeStateContext.Provider>
    );
};
